import React, {PropsWithChildren} from 'react';
import cn from "classnames";
import {useTranslation} from 'react-i18next';
import {FieldError, RegisterOptions, UseFormMethods} from 'react-hook-form';

import './form.scss';

interface IFormSelectProps<FormValues, OptionType> {
    allowEmpty?: boolean,
    className?: string,
    dataCy?: string;
    defaultValue?: any;
    disabled?: boolean;
    form: UseFormMethods<FormValues>
    getOptionLabel: (option: OptionType) => string;
    getOptionValue: (option: OptionType) => string | number;
    label: string;
    name: keyof FormValues & string;
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    options: OptionType[]
    registerOptions?: RegisterOptions;
}

export function FormSelect<FormType, OptionType>(props: PropsWithChildren<IFormSelectProps<FormType, OptionType>>) {
    const {t, i18n} = useTranslation();
    const {
        allowEmpty = true,
        className,
        defaultValue,
        disabled,
        form,
        getOptionLabel,
        getOptionValue,
        label,
        name,
        options,
        registerOptions,
    } = props;

    const error = form.errors[name] as FieldError;
    let errorMessage = error?.message;
    // If we weren't passed a custom message for this error, use a generic one for its type.
    if (error && !errorMessage) {
        switch(error.type) {
            case 'required':
                errorMessage = t('errors.validation.required');
                break;
            default:
                errorMessage = t('errors.validation.fallback', {errorType: error.type});
                break;
        }
    }

    return (
        <div className={cn(className, {
            formItem: true,
            error: !!error,
        })}>
            <div className="formItem_label">
                {label}
            </div>
            <select
                className="formItem_select"
                defaultValue={defaultValue}
                disabled={disabled}
                name={name}
                onChange={props.onChange}
                ref={form.register(registerOptions)}
                data-cy={props.dataCy}
            >
                {allowEmpty && <option value={undefined}> </option>}
                {options.map(o => {
                    const val = getOptionValue(o);
                    return (<option value={val} key={val}>{getOptionLabel(o)}</option>)
                })}
            </select>
            {error && (
                <div className="formItem_error">
                    {errorMessage}
                </div>
            )}
        </div>
    );
};